import { createSlice } from '@reduxjs/toolkit';
import request from '../../service';
import Exported from '../../service/endpoints';
import config from '../../service/env';

const initialState = {
  dataSection: '',
  researchParticipantId: '',
  fromResearch: false,
  fromResearchTable: false,
};

export const researchSlice = createSlice({
  name: 'researchData',
  initialState,
  reducers: {
    setDataSection: (state, action) => {
      state.dataSection = action.payload;
    },
    setResearchParticipantId: (state, action) => {
      state.researchParticipantId = action.payload;
    },
    setFromResearch: (state, action) => {
      state.fromResearch = action.payload;
    },
    setFromResearchTable: (state, action) => {
      state.fromResearchTable = action.payload;
    },
  },
});

export const { setDataSection, setResearchParticipantId, setFromResearch, setFromResearchTable } =
  researchSlice.actions;

// Training Module Actions
export const getResearchTrainingList = (data) => {
  const queryString =
    data && Object.keys(data)?.length ? `?${new URLSearchParams(data).toString()}` : '';
  return request({
    url: Exported.Endpoints.trainingModule + queryString,
    method: Exported.APIMethods.GET,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

export const getResearchTrainingByID = (id) => {
  return request({
    url: `${Exported.Endpoints.trainingModule}/${id}`,
    method: Exported.APIMethods.GET,
    baseUrl: config.analyticsServiceApi,
  });
};

export const createResearchTrainingModule = (data) => {
  return request({
    url: Exported.Endpoints.trainingModule,
    method: Exported.APIMethods.POST,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

export const updateResearchTrainingModule = (data, id) => {
  return request({
    url: `${Exported.Endpoints.trainingModule}/${id}`,
    method: Exported.APIMethods.PATCH,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

export const deleteResearchTrainingModule = (data) => {
  return request({
    url: `${Exported.Endpoints.trainingModule}/${data?.id}`,
    method: Exported.APIMethods.DELETE,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

// Lesson Module Actions
export const createResearchLessonModule = (data) => {
  return request({
    url: Exported.Endpoints.lessonModule,
    method: Exported.APIMethods.POST,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

export const updateResearchLessonModule = (data) => {
  return request({
    url: `${Exported.Endpoints.lessonModule}/${data?.id}`,
    method: Exported.APIMethods.PATCH,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

export const getResearchLessonListByID = (id, data) => {
  const queryString =
    data && Object.keys(data)?.length ? `?${new URLSearchParams(data).toString()}` : '';
  return request({
    url: Exported.Endpoints.getLessonsByID + id + queryString,
    method: Exported.APIMethods.GET,
    baseUrl: config.analyticsServiceApi,
  });
};

export const getResearchLessonByID = (data, id) => {
  const queryString =
    data && Object.keys(data)?.length ? `?${new URLSearchParams(data).toString()}` : '';
  return request({
    url: `${Exported.Endpoints.getLessonById}/${id}${queryString}`,
    method: Exported.APIMethods.GET,
    baseUrl: config.analyticsServiceApi,
  });
};

export const deleteResearchLessonModule = (data) => {
  return request({
    url: `${Exported.Endpoints.trainingModule}/lesson/${data?.id}`,
    method: Exported.APIMethods.DELETE,
    baseUrl: config.analyticsServiceApi,
    data,
  });
};

export const sendEventInvite = (data) => {
  return request({
    url: Exported.Endpoints.bulkInvite,
    method: Exported.APIMethods.POST,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

export const getEventsList = (data) => {
  const queryString =
    data && Object.keys(data)?.length ? `?${new URLSearchParams(data).toString()}` : '';
  return request({
    url: Exported.Endpoints.getEvent + queryString,
    method: Exported.APIMethods.GET,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

export const getCalendarEventList = (data) => {
  const queryString =
    data && Object.keys(data)?.length ? `?${new URLSearchParams(data).toString()}` : '';
  return request({
    url: Exported.Endpoints.livelihoodCalendarEvent + queryString,
    method: Exported.APIMethods.GET,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

export const createEventSubmit = (data) => {
  return request({
    url: Exported.Endpoints.createEvent,
    method: Exported.APIMethods.POST,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

export const getEventByID = (id) => {
  return request({
    url: Exported.Endpoints.getEventById + id,
    method: Exported.APIMethods.GET,
    baseUrl: config.analyticsServiceApi,
  });
};

export const patchEventByID = (id, data) => {
  return request({
    url: `${Exported.Endpoints.getEvent}/${id}`,
    method: Exported.APIMethods.PATCH,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

export const getParticipantList = (data) => {
  const queryString =
    data && Object.keys(data)?.length ? `?${new URLSearchParams(data).toString()}` : '';
  return request({
    url: Exported.Endpoints.participants + queryString,
    method: Exported.APIMethods.GET,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

export const getResearchParticipantsList = (data, baseUrl, endpoint) => {
  const queryString =
    data && Object.keys(data)?.length ? `?${new URLSearchParams(data).toString()}` : '';
  return request({
    url: endpoint + queryString,
    method: Exported.APIMethods.GET,
    data,
    baseUrl: baseUrl,
  });
};

export const getResearchCCWidgets = (data) => {
  return request({
    url: Exported.Endpoints.researchCCWidgets,
    method: Exported.APIMethods.GET,
    data,
    baseUrl: config.diffusionServiceApi,
  });
};

export const getResearchDFWidgets = (data) => {
  return request({
    url: Exported.Endpoints.researchDFWidgets,
    method: Exported.APIMethods.GET,
    baseUrl: config.diffusionServiceApi,
  });
};

// Training Module Actions
export const getParticipantsDetailsById = (id) => {
  return request({
    url: Exported.Endpoints.participantsById + `/${id}`,
    method: Exported.APIMethods.GET,
    baseUrl: config.analyticsServiceApi,
  });
};

export const getBulkInvite = (id, data) => {
  const queryString =
    data && Object.keys(data)?.length ? `?${new URLSearchParams(data).toString()}` : '';
  const url = id
    ? Exported.Endpoints.getBulkInvite + id + queryString
    : Exported.Endpoints.getBulkInvite + queryString;
  return request({
    url: url,
    method: Exported.APIMethods.GET,
    baseUrl: config.analyticsServiceApi,
  });
};

export const getAttendanceByEventId = (id, data) => {
  const queryString =
    data && Object.keys(data)?.length ? `?${new URLSearchParams(data).toString()}` : '';
  return request({
    url: Exported.Endpoints.getAttendanceById + id + queryString,
    method: Exported.APIMethods.GET,
    baseUrl: config.analyticsServiceApi,
  });
};

export const getResearchExportData = (data) => {
  const queryString = Object.keys(data).length ? `?${new URLSearchParams(data).toString()}` : '';
  return request({
    url: Exported.Endpoints.exportReportData + queryString,
    method: Exported.APIMethods.GET,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

export const updateAttendance = (data) => {
  return request({
    url: Exported.Endpoints.updateAttendance,
    method: Exported.APIMethods.PUT,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

export const getPillarFields = () => {
  return request({
    url: Exported.Endpoints.getPillarFields,
    method: Exported.APIMethods.GET,
    baseUrl: config.identityServiceApi,
  });
};

export const getReportByColumns = (data) => {
  // const queryString =
  //   data && Object.keys(data)?.length ? `?${new URLSearchParams(data).toString()}` : '';
  return request({
    url: Exported.Endpoints.getReportByColumns,
    method: Exported.APIMethods.POST,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

export const getAnalyticsforResearchTV = (data, endpoint) => {
  const queryString = Object.keys(data).length ? `?${new URLSearchParams(data).toString()}` : '';
  return request({
    url: endpoint + queryString,
    method: Exported.APIMethods.GET,
    data,
    baseUrl: config.traceabilityServiceApi,
  });
};

export const getAnalyticsforResearchDF = (data, endpoint) => {
  const queryString = Object.keys(data).length ? `?${new URLSearchParams(data).toString()}` : '';
  return request({
    url: endpoint + queryString,
    method: Exported.APIMethods.GET,
    data,
    baseUrl: config.diffusionServiceApi,
  });
};

export const getResearchDFCardData = () => {
  return request({
    url: Exported.Endpoints.researchDFCard,
    method: Exported.APIMethods.GET,
    baseUrl: config.diffusionServiceApi,
  });
};

export const getResearchAttendanceExportData = (data) => {
  return request({
    url: Exported.Endpoints.researchAttendance + data?.eventId,
    method: Exported.APIMethods.GET,
    params: {
      format: data?.format,
      fileName: data?.fileName,
      order: data?.order,
      take: data?.take,
    },
    responseType: 'blob',
    baseUrl: config.analyticsServiceApi,
    sendRes: true,
  });
};

export const deleteResearchSurveyById = (id) => {
  return request({
    url: Exported.Endpoints.deleteResearchSurvey + id,
    method: Exported.APIMethods.PUT,
    data: { activeStatus: false }, // only this in body
    baseUrl: config.diffusionServiceApi,
  });
};
const fetchCCDashboard = (data, endpoint) => {
  const queryString = Object.keys(data).length ? `?${new URLSearchParams(data).toString()}` : '';
  return request({
    url: endpoint + queryString,
    method: Exported.APIMethods.GET,
    data,
    baseUrl: config.complianceServiceApi,
  });
};

export const researchCCDashboard = (data, endpoint) => fetchCCDashboard(data, endpoint);
export const researchCCDashboardcard = (data, endpoint) => fetchCCDashboard(data, endpoint);

export const researchLIDashboard = (data, endpoint, apiType = 'li', signal) => {
  const baseUrls = {
    cc: config.complianceServiceApi,
    research: config.analyticsServiceApi,
    diffusion: config.diffusionServiceApi,
    tv: config.traceabilityServiceApi,
    li: config.livelihoodServiceApi,
  };

  const queryString = Object.keys(data).length ? `?${new URLSearchParams(data).toString()}` : '';

  return request({
    url: endpoint + queryString,
    method: Exported.APIMethods.GET,
    data,
    baseUrl: baseUrls[apiType],
    signal,
  });
};

export const getHomeDashboard = (data) => {
  console.log('Fetching Home Dashboard with params:', data);
  const queryString = Object.keys(data).length ? `?${new URLSearchParams(data).toString()}` : '';
  return request({
    url: Exported.Endpoints.homeDashboard + queryString,
    method: Exported.APIMethods.GET,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

export const exportResearchReports = (data, params) => {
  const queryString = Object.keys(params).length
    ? `?${new URLSearchParams(params).toString()}`
    : '';
  return request({
    url: Exported.Endpoints.downloadReports + queryString,
    method: Exported.APIMethods.POST,
    data,
    baseUrl: config.analyticsServiceApi,
  });
};

export const exportResearchDataset = (data) => {
  return request({
    url: Exported.Endpoints.downloadDataset,
    method: Exported.APIMethods.POST,
    data,
    baseUrl: config.analyticsServiceApi, // Assuming analytics service handles this, adjust if needed
  });
};
